import type { WidgetNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { NodeResizer, useReactFlow } from "@xyflow/react";
import { AnimatePresence } from "motion/react";
import { memo, useEffect, useRef, useState } from "react";
import { CANVAS_SNAP } from "../canvas-controls.config";
import { useCanvasApi } from "../context/CanvasContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";
import { WidgetFloatingToolbar } from "./WidgetFloatingToolbar";
import { WIDGET_ALL_PRESET_MAP, type WidgetPreset } from "./widgets";
import { resolvePresetId, widgetPresetId } from "./widgets/legacyPresets";
import { StickerArt } from "./widgets/StickerArt";

const [SNAP_X, SNAP_Y] = CANVAS_SNAP.grid;
const snapTo = (v: number, step: number) => Math.round(v / step) * step;

/**
 * Stored widget HTML with no registry preset behind it. Memoized so selection
 * and drag re-renders never touch the injected DOM. Chromeless like every
 * widget, and frozen by `.widget-legacy-html` (canvas-styles.css).
 */
const WidgetContent = memo(function WidgetContent({ html }: { html: string }) {
  return (
    <div
      className="widget-legacy-html"
      style={{ pointerEvents: "none", transformOrigin: "top left" }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

/**
 * A registry sticker. The SVG scales through its viewBox, so the node needs
 * no measuring pass: its size is the stored size, or the preset's before the
 * first write. Nothing paints behind the art; selection is the resizer frame.
 */
const StickerWidget = memo(function StickerWidget({
  id,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
  preset,
}: WidgetNodeProps & { preset: WidgetPreset }) {
  const canvas = useCanvasApi();
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const { canEdit } = canvas;
  const { updateNode } = useReactFlow();
  const { isResizingRef } = useCanvasUi();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);
  const resizeOriginRef = useRef<{ x: number; y: number } | null>(null);

  const nodeW = width || preset.defaultWidth;
  const nodeH = height || preset.defaultHeight;
  const minW = preset.resizable?.minWidth ?? 48;
  const minH = preset.resizable?.minHeight ?? 48;

  return (
    <>
      {canEdit && (
        <NodeResizer
          isVisible={!!selected && !dragging}
          minWidth={minW}
          minHeight={minH}
          keepAspectRatio
          onResizeStart={(_evt, params) => {
            resizeOriginRef.current = { x: params.x, y: params.y };
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            isResizingRef.current = false;
            const w = Math.max(minW, snapTo(params.width, SNAP_X));
            const h = Math.round(
              (w * preset.defaultHeight) / preset.defaultWidth,
            );
            const origin = resizeOriginRef.current;
            const x =
              origin && params.x !== origin.x
                ? params.x + params.width - w
                : params.x;
            const y =
              origin && params.y !== origin.y
                ? params.y + params.height - h
                : params.y;
            updateNode(id, { position: { x, y }, width: w, height: h });
            canvas.updateNodeDimensions(id, { width: w, height: h });
            canvas.updateNodePosition(id, { x, y });
          }}
        />
      )}

      <AnimatePresence>
        {selected &&
          selectedNodeCount === 1 &&
          !isMultiSelectToolbarOpen &&
          canEdit && (
            <CanvasFloatingToolbarPortal
              positionAbsoluteX={positionAbsoluteX}
              positionAbsoluteY={positionAbsoluteY}
              width={nodeW}
              topOffset={56}
            >
              <WidgetFloatingToolbar nodeId={id} />
            </CanvasFloatingToolbarPortal>
          )}
      </AnimatePresence>

      <div
        className={cn(
          isSectionPreviewMember &&
            "ring-2 ring-canvas-selection/60 rounded-lg",
        )}
        style={{ width: nodeW, height: nodeH }}
      >
        <StickerArt markup={preset.html ?? ""} scope={id} />
      </div>
    </>
  );
});

/**
 * Shell for React-component widgets.
 *
 * Reads the preset from WIDGET_ALL_PRESET_MAP to wire up:
 *   - NodeResizer with aspect-ratio lock and isResizingRef gating (when preset.resizable is set)
 *   - CSS scale wrapper so resizable component widgets fill the node bounds
 *   - WidgetFloatingToolbar
 *
 * Individual widget content components receive only their own concerns —
 * they never import node infrastructure.
 */
const ReactWidgetShell = memo(function ReactWidgetShell({
  id,
  data,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
  children,
}: WidgetNodeProps & { children: React.ReactNode }) {
  const canvas = useCanvasApi();
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const { canEdit } = canvas;
  const { updateNode } = useReactFlow();
  const { isResizingRef } = useCanvasUi();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  const preset = WIDGET_ALL_PRESET_MAP.get(String(data.presetId ?? ""));
  const resizeCfg = preset?.resizable;

  const resizeOriginRef = useRef<{ x: number; y: number } | null>(null);

  const defaultW = preset?.defaultWidth ?? 300;
  const defaultH = preset?.defaultHeight ?? 200;

  // ── Measure actual content size on first render ───────────────────────────
  //
  // Component widgets have a fixed intrinsic design size, but
  // preset.defaultHeight may not match the actual rendered height exactly.
  // We measure the DOM once and use the real size as the intrinsic reference.
  const contentRef = useRef<HTMLDivElement>(null);
  const [intrinsicSize, setIntrinsicSize] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const shellSyncedRef = useRef(false);
  // Once a node has been sized its dimensions live in storage; measuring
  // again on the next mount would only rewrite them. Nodes from before the
  // flag get synced once more, then carry it.
  const needsSizing = !width || !height || data.sized !== true;

  useEffect(() => {
    if (!resizeCfg || shellSyncedRef.current) return;
    let rafId: number;
    const measure = () => {
      const el = contentRef.current;
      if (!el) return;
      const { offsetWidth, offsetHeight } = el;
      if (offsetWidth > 0 && offsetHeight > 0) {
        setIntrinsicSize({ w: offsetWidth, h: offsetHeight });
      } else {
        rafId = requestAnimationFrame(measure);
      }
    };
    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync Loro once measured dimensions are known.
  useEffect(() => {
    if (!intrinsicSize || shellSyncedRef.current || !canEdit || !needsSizing)
      return;
    shellSyncedRef.current = true;

    let finalW = intrinsicSize.w;
    let finalH = intrinsicSize.h;

    if (width || height) {
      // Drag-placed: correct to measured aspect ratio.
      const dragW = width || intrinsicSize.w;
      const dragH = height || intrinsicSize.h;
      const s = Math.min(dragW / intrinsicSize.w, dragH / intrinsicSize.h);
      finalW = Math.round(intrinsicSize.w * s);
      finalH = Math.round(intrinsicSize.h * s);
    }

    // Measuring is not an edit: undo must not step through it.
    canvas.withoutUndo(() => {
      if (
        Math.abs(finalW - (width || defaultW)) > 1 ||
        Math.abs(finalH - (height || defaultH)) > 1
      ) {
        updateNode(id, { width: finalW, height: finalH });
        canvas.updateNodeDimensions(id, { width: finalW, height: finalH });
      }
      canvas.updateNodeData(id, { sized: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intrinsicSize]);

  // ── Dimensions for rendering ──────────────────────────────────────────────
  // Measured intrinsic size; preset defaults until measured.
  const intrinsicW = intrinsicSize?.w ?? defaultW;
  const intrinsicH = intrinsicSize?.h ?? defaultH;
  const nodeW = width || intrinsicW;
  const nodeH = height || intrinsicH;

  const scale = resizeCfg
    ? Math.min(nodeW / intrinsicW, nodeH / intrinsicH)
    : 1;
  // Defer the scaled state until intrinsic size is measured so the component
  // can render at its natural height first. Without this, the outer div is
  // clamped to preset.defaultHeight before the actual component height is
  // measured, clipping the content and blocking the measurement ref.
  const isScaled =
    resizeCfg != null && nodeW > 0 && nodeH > 0 && intrinsicSize !== null;

  return (
    <>
      {resizeCfg && canEdit && (
        <NodeResizer
          isVisible={!!selected && !dragging}
          minWidth={resizeCfg.minWidth}
          minHeight={resizeCfg.minHeight}
          keepAspectRatio
          onResizeStart={(_evt, params) => {
            resizeOriginRef.current = { x: params.x, y: params.y };
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            isResizingRef.current = false;
            const minW = resizeCfg.minWidth ?? 48;
            const minH = resizeCfg.minHeight ?? 48;
            // Snap width to grid; derive height exactly from the intrinsic
            // ratio so handles never drift from visual content.
            const w = Math.max(minW, snapTo(params.width, SNAP_X));
            const h = intrinsicSize
              ? Math.max(
                  minH,
                  Math.round(intrinsicSize.h * (w / intrinsicSize.w)),
                )
              : Math.max(minH, snapTo(params.height, SNAP_Y));
            const origin = resizeOriginRef.current;
            const x =
              origin && params.x !== origin.x
                ? params.x + params.width - w
                : params.x;
            const y =
              origin && params.y !== origin.y
                ? params.y + params.height - h
                : params.y;
            updateNode(id, { position: { x, y }, width: w, height: h });
            canvas.updateNodeDimensions(id, { width: w, height: h });
            canvas.updateNodePosition(id, { x, y });
          }}
        />
      )}

      <AnimatePresence>
        {selected &&
          selectedNodeCount === 1 &&
          !isMultiSelectToolbarOpen &&
          canEdit && (
            <CanvasFloatingToolbarPortal
              positionAbsoluteX={positionAbsoluteX}
              positionAbsoluteY={positionAbsoluteY}
              width={nodeW}
              topOffset={56}
            >
              <WidgetFloatingToolbar nodeId={id} />
            </CanvasFloatingToolbarPortal>
          )}
      </AnimatePresence>

      {/* Outer container — fills node dimensions when scaled */}
      <div
        className={cn(
          isSectionPreviewMember &&
            "ring-2 ring-canvas-selection/60 rounded-lg",
        )}
        style={
          isScaled
            ? {
                width: nodeW,
                height: nodeH,
                overflow: "hidden",
                position: "relative",
              }
            : { width: "fit-content", height: "fit-content" }
        }
      >
        {/* Scale wrapper. contentRef measures actual height before first Loro sync. */}
        <div
          ref={!isScaled ? contentRef : undefined}
          style={
            isScaled
              ? {
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  width: intrinsicW,
                  height: intrinsicH,
                }
              : undefined
          }
        >
          {children}
        </div>
      </div>
    </>
  );
});

/**
 * Data-driven routing wrapper.
 *
 * If the preset declares a `component`, that component is rendered inside
 * ReactWidgetShell (which handles toolbar, NodeResizer, CSS scale, etc.).
 * A sticker preset renders through StickerWidget. Legacy preset ids resolve
 * to their stickers first, as do preset-less placements by label. Stored HTML with no preset takes the frozen
 * HTML / CSS-scale path (WidgetNodeImpl).
 *
 * Adding a new React widget never requires changing this file — just declare
 * `component` on the preset and register it in widgets/index.ts.
 */
export const WidgetNode = memo(function WidgetNode(props: WidgetNodeProps) {
  const preset = WIDGET_ALL_PRESET_MAP.get(
    resolvePresetId(widgetPresetId(props.data)),
  );
  if (preset?.component) {
    const Comp = preset.component;
    return (
      <ReactWidgetShell {...props}>
        <Comp {...props} />
      </ReactWidgetShell>
    );
  }
  if (preset?.html) return <StickerWidget {...props} preset={preset} />;
  return <WidgetNodeImpl {...props} />;
});

const WidgetNodeImpl = memo(function WidgetNodeImpl({
  id,
  data,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
}: WidgetNodeProps) {
  const canvas = useCanvasApi();
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const { canEdit } = canvas;
  const { updateNode } = useReactFlow();
  const { isResizingRef } = useCanvasUi();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  // ── Two-phase CSS-scale resize ────────────────────────────────────────────
  //
  // Phase 1 (measurement): render at fit-content to read intrinsic dimensions.
  // Phase 2 (scaled):      render at node width×height with CSS scale applied.
  //
  // This lets widgets authored at a fixed size (e.g. 360px wide) still fill
  // arbitrary node dimensions cleanly on the canvas, without reflowing their
  // HTML layout. The scale preserves the widget's original design intent.

  const contentRef = useRef<HTMLDivElement>(null);
  const [intrinsicSize, setIntrinsicSize] = useState<{
    w: number;
    h: number;
  } | null>(null);

  useEffect(() => {
    let rafId: number;
    const measure = () => {
      const el = contentRef.current;
      if (!el) return;
      const { offsetWidth, offsetHeight } = el;
      if (offsetWidth > 0 && offsetHeight > 0) {
        setIntrinsicSize({ w: offsetWidth, h: offsetHeight });
      } else {
        // Element not yet laid out — retry next frame
        rafId = requestAnimationFrame(measure);
      }
    };
    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
    // Re-measure when HTML changes
  }, [data.html]);

  // ── Sync node to aspect-correct dimensions on first measurement ──────────
  //
  // Runs once after the intrinsic HTML size is measured and corrects the node
  // dimensions so that scale = 1.0 (single-click) or the content exactly fills
  // the node boundary (drag-to-place).
  //
  // Single-click: no explicit width/height → use intrinsic size directly.
  // Drag-to-place: width AND height are already set from the drag rectangle,
  //   but they may not match the intrinsic aspect ratio, leaving a transparent
  //   gap on one axis. We lock to the intrinsic ratio using the limiting axis.
  //
  // Once per node, not per mount: a sized node carries `data.sized` and its
  // dimensions, and a viewer never writes.
  const syncedRef = useRef(false);
  const needsSizing = !width || !height || data.sized !== true;
  useEffect(() => {
    if (!intrinsicSize || syncedRef.current || !canEdit || !needsSizing) return;
    syncedRef.current = true;

    let finalW = intrinsicSize.w;
    let finalH = intrinsicSize.h;

    if (width || height) {
      // Drag placement: user drew a rectangle — correct both dims to the
      // intrinsic aspect ratio so the content fills the full node boundary.
      const dragW = width || intrinsicSize.w;
      const dragH = height || intrinsicSize.h;
      const s = Math.min(dragW / intrinsicSize.w, dragH / intrinsicSize.h);
      finalW = Math.round(intrinsicSize.w * s);
      finalH = Math.round(intrinsicSize.h * s);
    }

    canvas.withoutUndo(() => {
      updateNode(id, { width: finalW, height: finalH });
      canvas.updateNodeDimensions(id, { width: finalW, height: finalH });
      canvas.updateNodeData(id, { sized: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intrinsicSize]);

  // Actual node dimensions from React Flow. Use || (not ??) so React Flow's
  // initial width=0 (before measurement) also falls back to intrinsic size —
  // the same fix applied to EntityNode. For single-click placements with no
  // explicit dimensions, width is undefined and intrinsicSize drives the size.
  const targetW = width || intrinsicSize?.w || 0;
  const targetH = height || intrinsicSize?.h || 0;

  const scaleX = intrinsicSize && targetW > 0 ? targetW / intrinsicSize.w : 1;
  const scaleY = intrinsicSize && targetH > 0 ? targetH / intrinsicSize.h : 1;
  // Uniform scale so the content is never distorted. keepAspectRatio on the
  // NodeResizer ensures both axes always change together, so scaleX === scaleY
  // after the initial placement sync.
  const scale = Math.min(scaleX, scaleY);

  const isScaled = !!intrinsicSize && targetW > 0 && targetH > 0;

  // Track resize origin so we can compensate position when a left/top handle
  // is used — identical to ShapeNode's pattern which fixes the choppiness.
  const resizeOriginRef = useRef<{ x: number; y: number } | null>(null);

  return (
    <>
      {/* NodeResizer — aspect-ratio locked using measured intrinsic dimensions.
          keepAspectRatio prevents the transparent-gap problem caused by the
          uniform CSS scale: both axes always change together so the content
          fills the full node bounds at any size.
          isResizingRef gates useCanvasOnNodesChange from writing to Loro during
          live drag (matching EntityNode) — historically prevented a ~50Hz
          oscillation when keepAspectRatio fought RF's snapGrid; RF snapping
          is off now (widgets resize smoothly and settle on release) but the
          write gate stays. */}
      {canEdit && (
        <NodeResizer
          isVisible={!!selected && !dragging}
          minWidth={96}
          minHeight={48}
          keepAspectRatio
          onResizeStart={(_evt, params) => {
            resizeOriginRef.current = { x: params.x, y: params.y };
            isResizingRef.current = true;
          }}
          onResizeEnd={(_evt, params) => {
            isResizingRef.current = false;
            // Snap width to grid. Derive height from the measured intrinsic
            // ratio so handles stay exactly flush with visual content across
            // repeated resizes. Snapping both axes independently accumulates
            // ratio drift that makes the handles and content diverge over time.
            const w = Math.max(96, snapTo(params.width, SNAP_X));
            const h = intrinsicSize
              ? Math.max(
                  48,
                  Math.round(intrinsicSize.h * (w / intrinsicSize.w)),
                )
              : Math.max(48, snapTo(params.height, SNAP_Y));
            const origin = resizeOriginRef.current;

            // When a left/top handle shifts the origin, compensate position so
            // the non-dragged edge stays stable after snap quantisation.
            const x =
              origin && params.x !== origin.x
                ? params.x + params.width - w
                : params.x;
            const y =
              origin && params.y !== origin.y
                ? params.y + params.height - h
                : params.y;

            updateNode(id, { position: { x, y }, width: w, height: h });
            canvas.updateNodeDimensions(id, { width: w, height: h });
            canvas.updateNodePosition(id, { x, y });
          }}
        />
      )}

      {/* Floating toolbar — shown for single selection; suppressed when
          MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2). */}
      <AnimatePresence>
        {selected &&
          selectedNodeCount === 1 &&
          !isMultiSelectToolbarOpen &&
          canEdit &&
          isScaled && (
            <CanvasFloatingToolbarPortal
              positionAbsoluteX={positionAbsoluteX}
              positionAbsoluteY={positionAbsoluteY}
              width={targetW}
              topOffset={56}
            >
              <WidgetFloatingToolbar nodeId={id} />
            </CanvasFloatingToolbarPortal>
          )}
      </AnimatePresence>

      {/* Outer container — sized to the node's Loro dimensions once known.
          During Phase 1 (measurement) it's fit-content so we can read the
          intrinsic size. During Phase 2 it sizes to the target dimensions.
          overflow:visible so absolutely-positioned decorative elements
          (e.g. aura halos, sparkles) are not clipped by the bounding box. */}
      <div
        className={cn(
          isSectionPreviewMember &&
            "ring-2 ring-canvas-selection/60 rounded-lg",
        )}
        style={
          isScaled
            ? {
                width: targetW,
                height: targetH,
                overflow: "visible",
                position: "relative",
              }
            : {
                width: "fit-content",
                height: "fit-content",
                display: "inline-block",
              }
        }
      >
        {/* Scale wrapper — transforms content to fill target dimensions */}
        <div
          ref={contentRef}
          style={
            isScaled
              ? {
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  // Preserve intrinsic dimensions so the scale math stays correct
                  width: intrinsicSize!.w,
                  height: intrinsicSize!.h,
                }
              : undefined
          }
        >
          <WidgetContent html={data.html as string} />
        </div>
      </div>
    </>
  );
});
